/**
 * Executive Dashboard product page — BP-019/020.
 * Business health across six dimensions with elite/strong/healthy/watch bands.
 */
import { LayoutDashboard, Activity, TrendingUp, AlertTriangle, PieChart, FileBarChart } from "lucide-react";
import ProductPageTemplate, { ProductScreen } from "../ProductPageTemplate";

const heroMockup = (
  <ProductScreen title="dashboard / executive · last 30 days">
    <div className="grid grid-cols-4 gap-2 text-xs">
      {[
        ["Health", "90", "ELITE"],
        ["Revenue", "$427K", "+18%"],
        ["Close Rate", "38%", "+4pp"],
        ["Pipeline", "$1.8M", "27 deals"],
      ].map(([k, v, s]) => (
        <div key={k} className="rounded-lg border border-slate-200 p-2">
          <div className="uppercase text-[9px] text-slate-500">{k}</div>
          <div className="text-lg font-bold text-slate-900">{v}</div>
          <div className="text-[9px] text-emerald-600 font-semibold mt-0.5">{s}</div>
        </div>
      ))}
    </div>
    <div className="mt-3 space-y-1 text-[11px]">
      {[
        ["Acquisition", 92, "Elite"],
        ["Marketing", 87, "Strong"],
        ["Disposition", 84, "Strong"],
        ["Portfolio", 78, "Healthy"],
        ["Cash", 89, "Elite"],
        ["Team", 82, "Strong"],
      ].map(([k, v, b]) => (
        <div key={k} className="flex items-center gap-2">
          <div className="w-24 text-slate-600">{k}</div>
          <div className="flex-1 h-1.5 bg-slate-100 rounded">
            <div className="h-full bg-emerald-500 rounded" style={{ width: `${v}%` }} />
          </div>
          <div className="w-16 text-right font-mono text-slate-500">{v} · {b}</div>
        </div>
      ))}
    </div>
  </ProductScreen>
);

export default function ExecutiveDashboardPage() {
  return (
    <ProductPageTemplate
      slug="executive-dashboard"
      bp="BP-019 / BP-020 · Executive Dashboard"
      eyebrow="Product · Executive Dashboard"
      title="Know the health of your business"
      titleAccent="in seconds — not spreadsheets."
      subtitle="Forty-plus KPIs distilled into six business dimensions with elite / strong / healthy / watch bands. Every metric is deterministic, versioned, and drill-through to the underlying deals."
      bullets={[
        "40+ KPIs across Acquisition, Marketing, Disposition, Portfolio, Cash, and Team",
        "Threshold bands are configurable per tenant",
        "One-click export to PDF for lenders, LPs, or board decks",
      ]}
      heroMockup={heroMockup}
      capabilities={[
        { icon: LayoutDashboard, title: "Six Dimensions", desc: "Acquisition, Marketing, Disposition, Portfolio, Cash, Team — no dashboard soup, just clarity." },
        { icon: Activity, title: "Elite Bands", desc: "Every KPI is scored against benchmarked bands. Colour-coded instantly. No manual thresholds." },
        { icon: TrendingUp, title: "Drill-Through", desc: "Click any KPI → the underlying deals, calls, or docs. Root-cause every anomaly in seconds." },
        { icon: AlertTriangle, title: "Watchlist Alerts", desc: "Any KPI slipping into 'Watch' band triggers a proactive Slack/email digest." },
        { icon: PieChart, title: "Cohort Slicers", desc: "Split by market, source, strategy, or acquisitor. Find your winners and your leaks." },
        { icon: FileBarChart, title: "Board-Ready Exports", desc: "PDF snapshot with cited engine versions — regulator-friendly, LP-friendly." },
      ]}
      howItWorks={[
        { step: 1, title: "Aggregate", desc: "Every workspace, buyer, and campaign feeds the KPI catalog nightly (or on-demand)." },
        { step: 2, title: "Score", desc: "Each KPI is mapped to its benchmark band deterministically." },
        { step: 3, title: "Roll Up", desc: "Six dimensions produce one Business Health Score (0–100)." },
        { step: 4, title: "Alert", desc: "Slippage into a lower band fires a proactive notification — you never miss a leak." },
      ]}
      integrations={[
        { bp: "BP-019", name: "KPI Catalog" },
        { bp: "BP-020", name: "Dashboard Renderer" },
        { bp: "BP-005", name: "Deal Score" },
        { bp: "BP-021", name: "Cash Flow" },
        { bp: "BP-023", name: "Portfolio Intelligence" },
        { bp: "BP-025", name: "AI Coach Briefings" },
        { bp: "BP-026", name: "Governance" },
        { bp: "BP-018", name: "Marketing Attribution" },
      ]}
      proof={{
        stat: "3×",
        label: "BI dashboards replaced by one platform",
        quote: "The Executive Dashboard replaced three BI dashboards we ran every Monday morning. Same numbers, one page, one source of truth.",
        attribution: "Fund PM · Enterprise · 6 markets",
      }}
      related={[
        { title: "Portfolio Intelligence", to: "/products/portfolio-intelligence", desc: "Cash-flow health for held assets." },
        { title: "Market Intelligence", to: "/products/market-intelligence", desc: "ZIP-level health scoring for expansion." },
        { title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "AI-generated weekly briefings on your business." },
      ]}
    />
  );
}
